import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { User, Building2 } from "lucide-react";
import {
  MessageSquare,
  Database,
  Info,
  Home,
  Languages,
  Menu,
  X,
  Moon,
  Sun,
  PlusCircle,
} from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

interface HeaderProps {
  onClearChat?: () => void;
  onHome?: () => void;
  showNewChat?: boolean; // 控制是否显示新对话按钮
}

export const Header: React.FC<HeaderProps> = ({
  onClearChat,
  onHome,
  showNewChat = true,
}) => {
  const navigate = useNavigate();
  const { language, t, toggleLanguage } = useLanguage();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // 从 localStorage 读取主题偏好
    return localStorage.getItem("theme") === "dark";
  });

  // 切换深色模式
  const toggleDarkMode = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    localStorage.setItem("theme", newDarkMode ? "dark" : "light");

    if (newDarkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  };

  // 初始化主题
  React.useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [isDarkMode]);

  // 导航菜单项
  interface NavItem {
    label: string;
    icon: React.ReactNode;
    onClick: () => void;
  }

  const navItems: NavItem[] = [
    {
      label: t("首页", "Home"),
      icon: <Home className="w-4 h-4" />,
      onClick: () => {
        if (onHome) {
          onHome();
        } else {
          navigate("/");
        }
      },
    },
    {
      label: t("个人签证", "Personal Visa"),
      icon: <User className="w-4 h-4" />,
      onClick: () => navigate("/personal-visa"),
    },
    {
      label: t("企业服务", "Enterprise"),
      icon: <Building2 className="w-4 h-4" />,
      onClick: () => navigate("/enterprise"),
    },
    {
      label: t("关于我们", "About"),
      icon: <Info className="w-4 h-4" />,
      onClick: () => navigate("/about"),
    },
    {
      label: t("知识库", "Knowledge Base"),
      icon: <Database className="w-4 h-4" />,
      onClick: () => navigate("/knowledge-base"),
    },
  ];

  const handleNewChat = () => {
    if (onClearChat) {
      onClearChat();
    }
    // 如果在关于页面，先返回首页
    if (window.location.pathname !== "/") {
      navigate("/");
    }
  };

  return (
    <header className="bg-white dark:bg-[#0f172a] border-b border-gray-200 dark:border-gray-800 shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* 左侧：Logo 和品牌 */}
          <div className="flex items-center gap-4">
            {/* 移动端菜单按钮 */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label={t("菜单", "Menu")}
            >
              {isMenuOpen ? (
                <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              ) : (
                <Menu className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              )}
            </button>

            {/* Logo 区域 - 点击返回首页 */}
            <div
              onClick={onHome || (() => navigate("/"))}
              className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity group"
              title={t("返回首页", "Back to home")}
            >
              <div className="w-8 h-8 bg-gradient-to-br from-indigo-600 to-indigo-700 rounded-lg flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow">
                <MessageSquare className="w-4 h-4 text-white" />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-lg font-bold bg-gradient-to-r from-indigo-600 to-indigo-800 bg-clip-text text-transparent dark:from-indigo-400 dark:to-indigo-600">
                  {t("英国签证AI助手", "UK Visa AI Assistant")}
                </h1>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  UK Visa Assistant
                </p>
              </div>
            </div>

            {/* 桌面端导航菜单 */}
            <nav className="hidden lg:flex items-center gap-1 ml-6">
              {navItems.map((item, index) => (
                <button
                  key={index}
                  onClick={item.onClick}
                  className="flex items-center gap-1 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  {item.icon}
                  <span>{item.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* 右侧：功能按钮 */}
          <div className="flex items-center gap-2">
            {/* 深色模式切换 */}
            <button
              onClick={toggleDarkMode}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors group relative"
              title={isDarkMode ? t("切换到浅色模式", "Switch to light mode") : t("切换到深色模式", "Switch to dark mode")}
              aria-label={isDarkMode ? t("切换到浅色模式", "Switch to light mode") : t("切换到深色模式", "Switch to dark mode")}
            >
              {isDarkMode ? (
                <Sun className="w-5 h-5 text-gray-600 dark:text-gray-300 group-hover:text-indigo-600 transition-colors" />
              ) : (
                <Moon className="w-5 h-5 text-gray-600 dark:text-gray-300 group-hover:text-indigo-600 transition-colors" />
              )}
            </button>

            <button
              onClick={toggleLanguage}
              className="px-2.5 py-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors group inline-flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-300"
              title={t("切换到英文", "Switch to Chinese")}
              aria-label={t("切换到英文", "Switch to Chinese")}
            >
              <Languages className="w-5 h-5 text-gray-600 dark:text-gray-300 group-hover:text-indigo-600 transition-colors" />
              <span className="font-medium">{language === "zh" ? "EN" : "中文"}</span>
            </button>

            {/* 新对话按钮 */}
            {showNewChat && onClearChat && (
              <button
                onClick={handleNewChat}
                className="ml-2 px-3 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-sm hover:shadow-md inline-flex items-center gap-2"
                title={t("开始新的对话", "Start a new chat")}
                aria-label={t("新对话", "New chat")}
              >
                <PlusCircle className="w-4 h-4" />
                <span className="hidden sm:inline">{t("新对话", "New Chat")}</span>
              </button>
            )}
          </div>
        </div>

        {/* 移动端菜单 */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-200 dark:border-gray-700">
            <nav className="flex flex-col gap-1">
              {navItems.map((item, index) => (
                <button
                  key={index}
                  onClick={() => {
                    if (item.onClick) {
                      item.onClick();
                    }
                    setIsMenuOpen(false);
                  }}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  {item.icon}
                  <span>{item.label}</span>
                </button>
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
